import type { Ref } from "react";

import latinize from "latinize";

import Button from "@/design-system/button";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import type { IMapMarker } from "@Components/Map";
import { Panel } from "@Components/Panel";
import type { IRepeater } from "@Components/Repeater";
import { ItemTemplate, NoResultsTemplate } from "@Components/Repeater";
import RepeaterV2 from "@Components/Repeater/RepeaterV2";
import type { ITextbox } from "@Components/Textbox";
import { Textbox } from "@Components/Textbox";
import * as stylex from "@stylexjs/stylex";

import { colors } from "./../../styles/tokens.stylex";
import type { IShoppingMallTile } from "./ShoppingMallTile";
import { ShoppingMallTile } from "./ShoppingMallTile";

import * as css from "./ShoppingMallsAllPanel.module.css";

export interface IShoppingMallsAllPanel extends IComponentAjax<boolean> {
    title: string;
    allShoppingMallsLinkText: string;
    placeDescription: string;
    shoppingMallsRepeater: IRepeater;
    filter: ITextbox;
    panelRef?: Ref<HTMLDivElement>;
    onMapMarkerClick: (event: React.MouseEvent<HTMLDivElement>, mapMarker: IMapMarker) => void;
}

interface IShoppingMallsAllPanelState extends IComponentAjaxState {
    showAll: boolean;
    filterText: string | null;
    repeaterResults: IShoppingMallTile[] | null;
}

export class ShoppingMallsAllPanel extends ComponentAjax<
    boolean,
    IShoppingMallsAllPanel,
    IShoppingMallsAllPanelState
> {
    private get allMallsLink() {
        if (!this.props.placeDescription || this.state.showAll === true) {
            return null;
        }

        return (
            <Button as="button" size="M" variant="transparent" styleX={styles.showAll} onClick={this.showAll}>
                {this.props.allShoppingMallsLinkText}
            </Button>
        );
    }

    private get filterBox() {
        if (this.props.filter) {
            return (
                <Textbox
                    {...this.props.filter}
                    value={this.state.filterText || ""}
                    onChange={this.filterChange}
                />
            );
        }

        return null;
    }

    private get filteredResults() {
        if (!this.state.repeaterResults) {
            return undefined;
        }

        if (!this.state.filterText) {
            return this.state.repeaterResults.slice();
        }

        const filtered = this.state.repeaterResults.filter((item) => {
            if (!item.title || !this.state.filterText) {
                return false;
            }

            const titleLatinized = latinize(item.title);
            const filterLatinized = latinize(this.state.filterText);
            return titleLatinized.toLowerCase().indexOf(filterLatinized.toLowerCase()) > -1;
        });

        return filtered;
    }

    private filterUpdate: number | null;

    constructor(props: IShoppingMallsAllPanel) {
        super(props);

        this.state = {
            isLoading: false,
            showAll: false,
            filterText: null,
            repeaterResults:
                this.props.shoppingMallsRepeater && this.props.shoppingMallsRepeater.initialData
                    ? ({ ...this.props.shoppingMallsRepeater.initialData } as IShoppingMallTile[])
                    : null,
        };

        this.sendTrack = this.sendTrack.bind(this);
        this.showAll = this.showAll.bind(this);
        this.filterChange = this.filterChange.bind(this);
        this.repeaterLoaded = this.repeaterLoaded.bind(this);
    }

    public render(): JSX.Element {
        return (
            <div {...this.rootDefaultAttributes(css.shoppingMallsAllPanelComponent)}>
                {this.allMallsLink}
                {this.state.showAll !== false && (
                    <Panel title={this.props.title} className={css.allMallsPanel}>
                        <div className={css.mallFilter}>{this.filterBox}</div>
                        <RepeaterV2
                            {...this.props.shoppingMallsRepeater}
                            initialData={this.filteredResults}
                            success={this.repeaterLoaded}
                            noResultsTemplate={
                                <NoResultsTemplate>Brak wyników dla tego wyszukiwania</NoResultsTemplate>
                            }
                        >
                            <ItemTemplate>
                                <ShoppingMallTile {...({} as IShoppingMallTile)} />
                            </ItemTemplate>
                        </RepeaterV2>
                    </Panel>
                )}
            </div>
        );
    }

    protected ajaxSuccess(): void {}

    protected ajaxFail(): boolean {
        return false;
    }

    private repeaterLoaded(result: IShoppingMallTile[]): void {
        if (!result) {
            return;
        }

        const results = result.map((value: IShoppingMallTile) => ({
            ...value,
            key: value.id,
            onMapLinkClick: this.props.onMapMarkerClick,
        }));

        this.setState(() => ({ repeaterResults: results }));
    }

    private showAll(): void {
        if (this.state.showAll === true) {
            return;
        }

        this.setState({ showAll: true });
    }

    private sendTrack(): boolean {
        if (!this.state.filterText) {
            return true;
        }

        if (this.filterUpdate) {
            clearTimeout(this.filterUpdate);
        }

        this.filterUpdate = null;

        this.ajaxRequest({ searchText: this.state.filterText });

        return true;
    }

    private filterChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const { value } = event.target;

        if (this.filterUpdate) {
            clearTimeout(this.filterUpdate);
        }

        this.setState({ filterText: value });

        this.filterUpdate = window.setTimeout(this.sendTrack.bind(this), 2000);
    }
}

const styles = stylex.create({
    showAll: {
        color: colors.fg_Accent,
        textTransform: "uppercase",
        marginTop: "30px",
        width: "100%",
    },
});
